import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Assignment,
  AssignmentList,
  AssignmentResult,
  BulkAssignInput,
  BulkAssignResult,
  CreateAssignmentInput,
  FlagAssignmentProblemInput,
  MyWorkFilter,
  MyWorkItem,
  MyWorkList,
  OverrideAssignmentRateInput,
  ReassignInput,
  WorkActionResult,
  WorkloadList,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { kanbanKeys } from '../workflow/use-kanban.js';
import {
  bulkAssign,
  cancelAssignment,
  completeJob,
  createAssignment,
  flagJobProblem,
  getMyWork,
  getWorkload,
  listAssignments,
  listOpenProblems,
  overrideAssignmentRate,
  reassign,
  resolveAssignmentProblem,
  startJob,
  type AssignmentFilters,
} from './assignments-api.js';

/**
 * The data layer for both assignment screens.
 *
 * Nothing here is optimistic, unlike the board's stage moves. Two reasons, and they are the same
 * reason twice: an assignment CAPTURES A RATE, and a job completion CREATES AN EARNING. Neither
 * is a fact the browser can predict — the rate comes from a fallback chain resolved on the
 * server at the moment of the write — so showing a guess and correcting it a beat later would be
 * showing somebody a wage that was never real.
 *
 * Every write invalidates the board as well as the assignment lists: a card's assignee name and
 * its problem flag both live on the board's payload, and a name that does not appear until the
 * next refresh is a name the shop does not believe.
 */

export const assignmentKeys = {
  all: ['assignments'] as const,
  list: (filters: AssignmentFilters) => ['assignments', 'list', filters] as const,
  problems: () => ['assignments', 'problems'] as const,
  workload: () => ['assignments', 'workload'] as const,
  myWork: (filter: MyWorkFilter) => ['assignments', 'my-work', filter] as const,
};

/** Refresh everything a write could have moved: the lists, the workload panel and the board. */
function useAssignmentInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
    void queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
  };
}

export function useAssignments(
  filters: AssignmentFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<AssignmentList> {
  const client = useApiClient();
  return useQuery({
    queryKey: assignmentKeys.list(filters),
    queryFn: () => listAssignments(client, filters),
    enabled: options.enabled ?? true,
  });
}

/**
 * The owner's alert list. This is how "flagging a problem notifies the owner" reaches a screen:
 * the board banner and the order page both read it, so a blocked garment is visible wherever the
 * shop is already looking rather than only on the job it was raised against.
 */
export function useOpenProblems(
  options: { enabled?: boolean } = {},
): UseQueryResult<AssignmentList> {
  const client = useApiClient();
  return useQuery({
    queryKey: assignmentKeys.problems(),
    queryFn: () => listOpenProblems(client),
    enabled: options.enabled ?? true,
  });
}

export function useWorkload(options: { enabled?: boolean } = {}): UseQueryResult<WorkloadList> {
  const client = useApiClient();
  return useQuery({
    queryKey: assignmentKeys.workload(),
    queryFn: () => getWorkload(client),
    enabled: options.enabled ?? true,
  });
}

export function useCreateAssignment(): UseMutationResult<
  AssignmentResult,
  unknown,
  CreateAssignmentInput
> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: (input: CreateAssignmentInput) => createAssignment(client, input),
    onSuccess: invalidate,
  });
}

export function useBulkAssign(): UseMutationResult<BulkAssignResult, unknown, BulkAssignInput> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: (input: BulkAssignInput) => bulkAssign(client, input),
    onSuccess: invalidate,
  });
}

export function useReassign(): UseMutationResult<
  AssignmentResult,
  unknown,
  { assignmentId: string; input: ReassignInput }
> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: ({ assignmentId, input }: { assignmentId: string; input: ReassignInput }) =>
      reassign(client, assignmentId, input),
    onSuccess: invalidate,
  });
}

export function useOverrideRate(): UseMutationResult<
  Assignment,
  unknown,
  { assignmentId: string; input: OverrideAssignmentRateInput }
> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: OverrideAssignmentRateInput;
    }) => overrideAssignmentRate(client, assignmentId, input),
    onSuccess: invalidate,
  });
}

export function useResolveProblem(): UseMutationResult<
  Assignment,
  unknown,
  { assignmentId: string; note?: string }
> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: ({ assignmentId, note }: { assignmentId: string; note?: string }) =>
      resolveAssignmentProblem(client, assignmentId, note),
    onSuccess: invalidate,
  });
}

export function useCancelAssignment(): UseMutationResult<Assignment, unknown, string> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: (assignmentId: string) => cancelAssignment(client, assignmentId),
    onSuccess: invalidate,
  });
}

// --- The karigar's own screen --------------------------------------------------------------------

export function useMyWork(filter: MyWorkFilter): UseQueryResult<MyWorkList> {
  const client = useApiClient();
  return useQuery({
    queryKey: assignmentKeys.myWork(filter),
    queryFn: () => getMyWork(client, filter),
  });
}

/** Starting and finishing both move a garment, so both refresh the whole assignment tree. */
function useWorkAction(
  run: (client: ReturnType<typeof useApiClient>, assignmentId: string) => Promise<WorkActionResult>,
): UseMutationResult<WorkActionResult, unknown, string> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: (assignmentId: string) => run(client, assignmentId),
    onSuccess: invalidate,
  });
}

export function useStartJob(): UseMutationResult<WorkActionResult, unknown, string> {
  return useWorkAction(startJob);
}

export function useCompleteJob(): UseMutationResult<WorkActionResult, unknown, string> {
  return useWorkAction(completeJob);
}

export function useFlagProblem(): UseMutationResult<
  MyWorkItem,
  unknown,
  { assignmentId: string; input: FlagAssignmentProblemInput }
> {
  const client = useApiClient();
  const invalidate = useAssignmentInvalidation();
  return useMutation({
    mutationFn: ({
      assignmentId,
      input,
    }: {
      assignmentId: string;
      input: FlagAssignmentProblemInput;
    }) => flagJobProblem(client, assignmentId, input),
    onSuccess: invalidate,
  });
}
